<template>
  <form>
    <div class="row mb-2">
      <div class="col-sm-12">

        <div class="row mt-2">
          <label for="payInDealSumsEditable" class="col-sm-4 col-form-label  text-capitalize-first">
            {{ helper.trans('requisites.payInDealSumsEditable') }}
          </label>
          <div class="col-sm-8 form-check form-switch d-flex align-items-center">
            <input v-model="form.payInDealSumsEditable" id="payInDealSumsEditable" class="form-check-input" style="cursor: pointer" type="checkbox" role="switch"  >
          </div>
        </div>

        <div class="row mt-2">
          <label for="minPayInDealSum" class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.min_pay_in_deal_sum") }}</label>
          <div class="col-sm-3">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors('minPayInDealSum')">
              <input v-model="form.minPayInDealSum"
                     v-maska
                     :disabled="isMinSumFieldDisabled"
                     :class="[parentProps.class, {'bg-light': isMinSumFieldDisabled}]"
                     data-maska="9 99#"
                     data-maska-tokens="9:[0-9]:repeated"
                     data-maska-reversed
                     type="text" class="form-control" id="minPayInDealSum">
            </FormInputWrapper>
          </div>
        </div>

        <div class="row mt-2">
          <label for="maxPayInDealSum" class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.max_pay_in_deal_sum") }}</label>
          <div class="col-sm-3">
            <FormInputWrapper v-slot="parentProps" field="maxPayInDealSum" :form="componentName">
              <input v-model="form.maxPayInDealSum"
                     v-maska
                     :class="parentProps.class"
                     data-maska="9 99#"
                     data-maska-tokens="9:[0-9]:repeated"
                     data-maska-reversed
                     type="text" class="form-control" id="maxPayInDealSum">
            </FormInputWrapper>
          </div>
        </div>
      </div>
    </div>

  </form>
</template>

<script lang="ts">
import {defineComponent, toRefs, reactive, computed} from "vue";
import Multiselect from "vue-multiselect";
import {useStore} from "vuex";
import {helper} from "@/service/CommonHelper";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import RequisitesPayInDealSumsFormDTO from "@/models/form/RequisitesPayInDealSumsFormDTO";
import {api} from "@/service/APIClient";

const components = {
  Multiselect,
  FormInputWrapper
}

const componentName = 'RequisitesPayInDealSumsForm';
export default defineComponent({
  name: componentName,
  props: {},
  components,
  setup() {
    const store = useStore();
    const state = reactive({
      form: {} as RequisitesPayInDealSumsFormDTO,
      errors: {} as any,
    });

    const setErrors = (data: any) => {
      state.errors = {};
      for (const error of data) {
        if (!state.errors.hasOwnProperty(error.field)) {
          state.errors[error.field] = [];
        }
        state.errors[error.field].push(error)
      }
    }

    const getErrors = (field: string) => {
      return state.errors[field] || [];
    }

    const getForm = (): RequisitesPayInDealSumsFormDTO => {
      return state.form
    }

    const setForm = (form: RequisitesPayInDealSumsFormDTO) => {
      state.form = {
        ...form,
      }
    }

    const traderForLimits = computed(() => (state.form as any)?.trader ?? store.getters.getUser);

    const isMinSumFieldDisabled = computed(() => {
      const trader: any = traderForLimits.value;
      const currency = (state.form as any).currency;
      const minPayInDealSum = state.form.minPayInDealSum;
      
      if (!trader?.requisitesLimits?.length || !currency || !minPayInDealSum) {
        return false;
      }

      const traderLimit = trader.requisitesLimits.find((limit: { currency: string }) => 
        limit.currency === currency
      );
      
      if (!traderLimit) {
        return false;
      }

      const traderLimitAmount = Number.parseInt(traderLimit.amountLimitForDeal.replace(/\s/g, ''));
      const currentAmount = Number.parseInt(minPayInDealSum.replace(/\s/g, ''));
      
      return currentAmount > traderLimitAmount;
    });

    const save = () => {
      helper.clearErrors(componentName)
      const form = getForm();

      const trader: any = traderForLimits.value;
      const currency = (form as any).currency;
      const minPayInDealSum = form.minPayInDealSum;
      const maxPayInDealSum = form.maxPayInDealSum;
      
      if (trader?.requisitesLimits?.length && currency && minPayInDealSum) {
        const traderLimit = trader.requisitesLimits.find((limit: { currency: string }) => 
          limit.currency === currency
        );
        
        if (traderLimit) {
          const traderLimitAmount = Number.parseInt(traderLimit.amountLimitForDeal.replace(/\s/g, ''));
          const currentAmount = Number.parseInt(minPayInDealSum.replace(/\s/g, ''));
          
          if (currentAmount > traderLimitAmount) {
            setErrors([{ 
              field: 'minPayInDealSum', 
              message: `Минимальная сумма не может превышать лимит (${traderLimitAmount})` 
            }]);
            return Promise.reject(new Error('Validation failed'));
          }
        }
      }

      if (trader?.requisitesLimits?.length && currency && maxPayInDealSum) {
        const traderLimit = trader.requisitesLimits.find((limit: { currency: string }) => 
          limit.currency === currency
        );

        if (traderLimit && traderLimit.maxAmountLimitForDeal) {
          const traderLimitAmount = Number.parseInt(traderLimit.maxAmountLimitForDeal.replace(/\s/g, ''));
          const currentAmount = Number.parseInt(maxPayInDealSum.replace(/\s/g, ''));

          if (currentAmount > traderLimitAmount) {
            setErrors([{ 
              field: 'maxPayInDealSum', 
              message: `Максимальная сумма не может превышать лимит (${traderLimitAmount})` 
            }]);
            return Promise.reject(new Error('Validation failed'));
          }
        }
      }

      let promise;
      const data = {
        payInDealSumsEditable: form.payInDealSumsEditable,
        minPayInDealSum: form.minPayInDealSum ? Number.parseInt(form.minPayInDealSum!.replace(/\s/g, '')) : null,
        maxPayInDealSum: form.maxPayInDealSum ? Number.parseInt(form.maxPayInDealSum!.replace(/\s/g, '')) : null,
      };

      promise = api.updateRequisitesPayInDealSums(form.id!, data)

      promise
        .catch((e: any) => {
          if (e.response && e.response.status === 400 && e.response.data) {
            const errorMessage = e.response.data.error || e.response.data.message;
            if (errorMessage) {
              setErrors([{ field: 'minPayInDealSum', message: errorMessage }]);
            } else {
              helper.handleErrors(componentName, e);
            }
          } else {
            helper.handleErrors(componentName, e);
          }
        })

      return promise
    }

    return {
      ...toRefs(state),
      getForm,
      setForm,
      save,
      store,
      helper,
      componentName,
      isMinSumFieldDisabled,
      setErrors,
      getErrors
    }
  },
  async beforeMount() {
  }
})
</script>
